/* Client for the server-side multi-cost-center Actuals export API (REST, API Gateway + Cognito).
 *
 * Security rules for this file:
 * - The `Authorization` header carries the Cognito ID token, as the AppSync calls do.
 * - Nothing here logs. Errors carry only an HTTP status and a code: never the response body, the
 *   request URL or the presigned download URL.
 */
import { ACTUALS_EXPORT_API_BASE_URL_PATTERN, ACTUALS_EXPORT_API_URL_PARAMETER } from 'src/constants/actuals-export-constants';
import { ACTUALS_EXPORT_API_ERROR_CODES } from 'src/models/ActualsExportModels';
import type {
  ActualsExport,
  ActualsExportApiErrorCode,
  ActualsExportCreateRequest,
  ActualsExportDownload,
  ActualsExportErrorCode
} from 'src/models/ActualsExportModels';
import { isActualsExportStatus, isValidActualsExportId } from 'src/utils/actuals-export-utils';
import { getToken } from 'src/utils/auth-token-services';
import { retrieveParameterFromSSM } from 'src/utils/ssm-parameter';

const EXPORTS_PATH = '/actuals-exports';

type HttpMethod = 'GET' | 'POST';

interface ApiResponse {
  status: number;
  payload: unknown;
}

/** A failed export API call. The message is the code only, so it is safe to log. */
export class ActualsExportApiError extends Error {
  readonly status: number;
  readonly code: ActualsExportErrorCode;
  /** On `EXPORT_IN_PROGRESS`, the export that holds the lock. */
  readonly activeExport: ActualsExport | null;

  constructor(status: number, code: ActualsExportErrorCode, activeExport: ActualsExport | null = null) {
    super(`Actuals export request failed: ${code}`);
    Object.setPrototypeOf(this, ActualsExportApiError.prototype);
    this.name = 'ActualsExportApiError';
    this.status = status;
    this.code = code;
    this.activeExport = activeExport;
  }
}

export const isActualsExportApiError = (error: unknown): error is ActualsExportApiError => error instanceof ActualsExportApiError;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

const isApiErrorCode = (value: unknown): value is ActualsExportApiErrorCode =>
  typeof value === 'string' && (ACTUALS_EXPORT_API_ERROR_CODES as readonly string[]).includes(value);

const nullableString = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const nullableNumber = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

/** Maps the snake_case Export object; returns null when it is not one. */
const parseExport = (value: unknown): ActualsExport | null => {
  if (!isRecord(value) || !isValidActualsExportId(value.export_id) || !isActualsExportStatus(value.status)) {
    return null;
  }
  const costCenters = Array.isArray(value.cost_centers) ? value.cost_centers.filter((cc): cc is string => typeof cc === 'string') : [];
  const costCenterCount = nullableNumber(value.cost_center_count) ?? costCenters.length;
  return {
    exportId: value.export_id,
    status: value.status,
    businessGroup: nullableString(value.business_group),
    costCenterCount,
    costCenters,
    fileName: nullableString(value.file_name),
    rowCount: nullableNumber(value.row_count),
    fileSize: nullableNumber(value.file_size),
    dataAsOf: nullableString(value.data_as_of),
    createdAt: nullableString(value.created_at),
    startedAt: nullableString(value.started_at),
    finishedAt: nullableString(value.finished_at),
    errorCode: nullableString(value.error_code)
  };
};

/** Reads `{"export": Export}`, or throws INVALID_RESPONSE. */
const requireExport = ({ status, payload }: ApiResponse): ActualsExport => {
  const parsed = isRecord(payload) ? parseExport(payload.export) : null;
  if (!parsed) {
    throw new ActualsExportApiError(status, 'INVALID_RESPONSE');
  }
  return parsed;
};

/** Code for an error response. Bodies without a known code (gateway or WAF responses) map by status. */
const toApiError = (status: number, payload: unknown): ActualsExportApiError => {
  const code = isRecord(payload) ? payload.error : undefined;
  if (isApiErrorCode(code)) {
    const activeExport = code === 'EXPORT_IN_PROGRESS' && isRecord(payload) ? parseExport(payload.export) : null;
    return new ActualsExportApiError(status, code, activeExport);
  }
  if (status === 401) {
    return new ActualsExportApiError(status, 'UNAUTHENTICATED');
  }
  if (status === 429) {
    return new ActualsExportApiError(status, 'THROTTLED');
  }
  return new ActualsExportApiError(status, 'UNKNOWN');
};

const readJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const requireExportId = (exportId: string): string => {
  if (!isValidActualsExportId(exportId)) {
    throw new ActualsExportApiError(400, 'INVALID_REQUEST');
  }
  return encodeURIComponent(exportId);
};

export class ActualsExportService {
  private baseUrlPromise: Promise<string> | null = null;
  private lastCreateRequest: ActualsExportCreateRequest | null = null;

  /** Starts an export. A 409 throws EXPORT_IN_PROGRESS with the export that is already running. */
  async createExport(body: ActualsExportCreateRequest): Promise<ActualsExport> {
    this.lastCreateRequest = body;
    return requireExport(await this.request('POST', EXPORTS_PATH, body));
  }

  /** Starts the last requested export again (the Retry action). */
  async retryLastExport(): Promise<ActualsExport> {
    if (!this.lastCreateRequest) {
      throw new ActualsExportApiError(0, 'UNKNOWN');
    }
    return this.createExport(this.lastCreateRequest);
  }

  /** True when this tab has an export request it can send again. */
  hasRetryableRequest(): boolean {
    return this.lastCreateRequest !== null;
  }

  /** The caller's queued or running export, or null (204) when there is none. */
  async getActiveExport(): Promise<ActualsExport | null> {
    const response = await this.request('GET', `${EXPORTS_PATH}/active`);
    return response.status === 204 ? null : requireExport(response);
  }

  async getExport(exportId: string): Promise<ActualsExport> {
    return requireExport(await this.request('GET', `${EXPORTS_PATH}/${requireExportId(exportId)}`));
  }

  /** Aborts a queued or running export. A 409 throws NOT_IN_PROGRESS when the job already finished. */
  async abortExport(exportId: string): Promise<ActualsExport> {
    return requireExport(await this.request('POST', `${EXPORTS_PATH}/${requireExportId(exportId)}/abort`));
  }

  /** A fresh, short-lived presigned URL. The caller must validate it before navigating. */
  async getDownloadUrl(exportId: string): Promise<ActualsExportDownload> {
    const { status, payload } = await this.request('GET', `${EXPORTS_PATH}/${requireExportId(exportId)}/download`);
    if (!isRecord(payload) || typeof payload.url !== 'string' || typeof payload.file_name !== 'string') {
      throw new ActualsExportApiError(status, 'INVALID_RESPONSE');
    }
    return { url: payload.url, expiresIn: nullableNumber(payload.expires_in) ?? 0, fileName: payload.file_name };
  }

  /** The API base URL from SSM, cached for the session. A failed lookup is retried on the next call. */
  private getBaseUrl(): Promise<string> {
    if (!this.baseUrlPromise) {
      this.baseUrlPromise = retrieveParameterFromSSM(ACTUALS_EXPORT_API_URL_PARAMETER)
        .catch((): string => '')
        .then((value) => {
          const trimmed = value.trim();
          if (!ACTUALS_EXPORT_API_BASE_URL_PATTERN.test(trimmed)) {
            throw new ActualsExportApiError(0, 'NOT_CONFIGURED');
          }
          return trimmed.replace(/\/+$/, '');
        });
      this.baseUrlPromise.catch(() => {
        this.baseUrlPromise = null;
      });
    }
    return this.baseUrlPromise;
  }

  private async request(method: HttpMethod, path: string, body?: ActualsExportCreateRequest): Promise<ApiResponse> {
    const baseUrl = await this.getBaseUrl();

    let idToken: string;
    try {
      idToken = await getToken();
    } catch {
      throw new ActualsExportApiError(401, 'UNAUTHENTICATED');
    }

    const headers: Record<string, string> = { Authorization: idToken };
    if (body) {
      headers['Content-Type'] = 'application/json';
    }

    let response: Response;
    try {
      response = await fetch(`${baseUrl}${path}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        cache: 'no-store',
        credentials: 'omit',
        redirect: 'error',
        referrerPolicy: 'no-referrer'
      });
    } catch {
      throw new ActualsExportApiError(0, 'NETWORK_ERROR');
    }

    if (response.status === 204) {
      return { status: 204, payload: null };
    }
    const payload = await readJson(response);
    if (!response.ok) {
      throw toApiError(response.status, payload);
    }
    return { status: response.status, payload };
  }
}

/** Shared instance: one SSM lookup and one remembered request per tab. */
export const actualsExportService = new ActualsExportService();
